<script setup lang="ts">
/**
 * Un recibo: sus líneas, y el cobro.
 *
 * **No usa el add/edit de la plantilla.** Ahí cada ítem es un bloque de campos
 * con su propia tarjeta, y eso funciona para una factura de tres conceptos
 * redactados a mano; acá las líneas salen de actividades ya reservadas y suelen
 * ser diez o veinte. Como filas de tabla se leen de corrido y se comparan entre
 * sí, que es lo que hace quien está cobrando.
 *
 * Cada fila nueva permite elegir un **service**, un **rental** o escribir una
 * línea libre, con su cantidad y su precio.
 */
import { listOfferings } from '@/api/offerings'
import { apiOrigin } from '@/api/client'
import {
  type OutstandingLine,
  type Receipt,
  type ReceiptLine,
  createReceipt,
  createReceiptPdfLink,
  deleteReceipt,
  getOutstandingCharges,
  getReceipt,
  payReceipt,
  updateReceipt,
  voidReceipt,
} from '@/api/receipts'
import { receiptRows } from '@/data/receiptRows'
import AlertBox from '@/components/AlertBox.vue'
import ConfirmDialog from '@/components/ConfirmDialog.vue'
import { useCurrentDiveCenter } from '@/composables/useCurrentDiveCenter'
import { usePreferences } from '@/composables/usePreferences'
import { apiMessage, notifyError, notifySuccess, notifyWarning } from '@/composables/useSnackbar'
import type { Offering } from '@/types'

definePage({
  meta: {
    requiresAuth: true,

    // Sin item propio en el menú: se marca Receipts (§30.5 explica por qué esto
    // tiene que estar en el `meta` del registro y no asignarse en el setup).
    navActiveLink: 'sales-receipts',
  },
})

const route = useRoute('sales-receipts-receipt-id')
const router = useRouter()

/*
 * `new` es la ruta del borrador **todavía sin guardar**.
 *
 * Comparte pantalla con el recibo existente en vez de tener un `new.vue` propio:
 * es exactamente el mismo formulario y duplicarlo dejaría dos copias que se
 * desincronizan al primer arreglo. Un id de recibo es un ObjectId de 24 hex, así
 * que "new" nunca colisiona.
 */
const isNew = computed(() => String(route.params.receiptId) === 'new')
const receiptId = ref(String(route.params.receiptId))
const clientId = ref(String(route.query.clientId ?? ''))
const clientName = ref('')

const { currentId } = useCurrentDiveCenter()
const { formatCurrency, currencyPrefix, formatDate } = usePreferences()

const receipt = ref<Receipt | null>(null)

// `OutstandingLine` y no `ReceiptLine`: los renglones que llegan sin precio
// traen `needsPrice` para poder marcarlos. No se guarda — el backend lo
// descarta al escribir el recibo.
const lines = ref<OutstandingLine[]>([])
const notes = ref('')
const discount = ref(0)
const offerings = ref<Offering[]>([])

const loading = ref(true)
const saving = ref(false)
const error = ref('')
const confirmRef = ref<InstanceType<typeof ConfirmDialog> | null>(null)

const editable = computed(() => isNew.value || receipt.value?.status === 'draft')
const currency = computed(() => receipt.value?.currency ?? draftCurrency.value)

/*
 * Como en el resto del portal, la moneda se escribe como diga Preferences. El
 * recibo no es una excepción: el listado, el editor, el preview y el PDF leen
 * todos la misma preferencia, así que el mismo cobro se escribe igual en los
 * cuatro sin necesidad de forzar nada.
 */
const money = (amount: number) => formatCurrency(amount, currency.value)

/**
 * La salida de la que vino una línea. Vacío en las cargadas a mano.
 *
 * El título solo se repite cuando aporta: en la línea de la actividad es el
 * mismo texto que el rótulo, y en un alquiler dice a qué salida acompañó.
 */
/**
 * El chip de tipo de cada renglón: mismo lenguaje que el panel del catálogo.
 *
 * Con diez o veinte líneas seguidas, "BCD" y "Night dives" se leen igual y hay
 * que reconstruir de memoria cuál es la actividad y cuál el equipo. El chip lo
 * dice de un vistazo y con el mismo color e ícono que tienen Services y Rentals
 * en el menú, así no hay un segundo código visual que aprender.
 *
 * `other` es una línea escrita a mano — no salió del catálogo y no tiene tipo
 * que declarar más allá de eso.
 */
function lineKind(line: OutstandingLine): { label: string; color: string; icon: string } {
  if (line.kind === 'rental')
    return { label: 'Rental', color: 'info', icon: 'ri-price-tag-3-line' }

  if (line.kind === 'activity')
    return { label: 'Service', color: 'primary', icon: 'ri-calendar-2-line' }

  return { label: 'Manual', color: 'secondary', icon: 'ri-pencil-line' }
}

/**
 * De qué salida vino el renglón (§57).
 *
 * La fecha y la hora se quedan acá: es un renglón que ya existe, y sacarlas a una
 * cabecera por día costaba una fila entera por día, en una tabla donde el
 * problema era justamente el alto.
 *
 * El TÍTULO sí se va: en la actividad es el mismo texto que el rótulo, y en un
 * alquiler ya lo dice la fila de la que cuelga. Lo lleva solo el que no tiene su
 * actividad en el recibo, que no tiene de quién colgarse.
 */
function lineDetail(line: OutstandingLine, child: boolean): string {
  if (!line.activityDate)
    return ''

  const parts = [formatDate(line.activityDate)]

  if (line.activityTime)
    parts.push(line.activityTime)

  const orphan = child && line.kind !== 'activity' && !hasParent(line)

  if (orphan && line.activityTitle && line.activityTitle !== line.label)
    parts.push(line.activityTitle)

  return parts.join(' · ')
}

/** ¿Su actividad está en el recibo? De eso depende si hay que nombrarla. */
function hasParent(line: OutstandingLine): boolean {
  return !!line.bookingId
    && lines.value.some(l => l.kind === 'activity' && l.bookingId === line.bookingId)
}

/**
 * Las filas como se dibujan: cabecera de día, actividad, y sus alquileres
 * tabulados debajo (§57). Cada una lleva su índice real, que es con el que se
 * edita y se borra.
 */
const rows = computed(() => receiptRows(lines.value))
const draftCurrency = ref('EUR')

const subtotal = computed(() =>
  Math.round(lines.value.reduce((sum, l) => sum + l.unitAmount * l.quantity, 0) * 100) / 100,
)

/*
 * El descuento se topea al subtotal, igual que en el servidor: si el usuario
 * escribe 900 sobre un subtotal de 800, el total no puede quedar en -100 mientras
 * el backend guarda 0. Los dos lados tienen que decir lo mismo mientras se
 * escribe.
 */
const appliedDiscount = computed(() =>
  Math.min(Math.max(Number(discount.value) || 0, 0), Math.max(subtotal.value, 0)),
)

const total = computed(() =>
  Math.round((subtotal.value - appliedDiscount.value) * 100) / 100,
)

/**
 * El catálogo del centro, para el panel de selección.
 *
 * Se separa en servicios y alquileres —y se rotula— porque en el mostrador son
 * dos cosas distintas: uno es lo que se hizo, el otro lo que se llevó.
 */
interface OfferingOption {
  id: string
  title: string
  kind: 'service' | 'rental'
  amount: number
}

const offeringOptions = computed<OfferingOption[]>(() =>
  offerings.value
    .filter(o => o.active)
    .map(o => ({
      id: o.id,
      title: o.title,
      kind: o.kind === 'product' ? ('rental' as const) : ('service' as const),
      amount: o.price?.amount ?? 0,
    }))
    .sort((a, b) => a.title.localeCompare(b.title)),
)

async function load() {
  const dcId = currentId.value

  if (!dcId)
    return

  loading.value = true
  error.value = ''
  try {
    const offRes = await listOfferings(dcId)

    offerings.value = offRes.offerings

    if (isNew.value) {
      /*
       * Borrador nuevo: se trae lo pendiente y se arma EN MEMORIA. Nada se
       * escribe hasta que el usuario guarda — equivocarse de cliente y cerrar la
       * pantalla no puede dejar un documento tirado en el listado.
       */
      if (!clientId.value) {
        error.value = 'No client selected. Start the receipt from the list.'

        return
      }

      const outstanding = await getOutstandingCharges(dcId, clientId.value)

      clientName.value = outstanding.clientName
      draftCurrency.value = outstanding.currency
      lines.value = outstanding.activities.flatMap(a => a.lines.map(l => ({ ...l })))

      /*
       * Ya vienen como renglón en cero, así que el aviso dice qué completar y no
       * qué falta: antes pedía agregarlas a mano, que era pedirle a quien cobra
       * que reconstruyera de memoria cuáles eran.
       */
      if (outstanding.unpricedCount) {
        notifyWarning(
          `${outstanding.unpricedCount} activit${outstanding.unpricedCount === 1 ? 'y has' : 'ies have'} `
          + 'no price in the catalogue — type the amount on the highlighted lines.',
        )
      }

      return
    }

    const res = await getReceipt(dcId, receiptId.value)

    receipt.value = res.receipt
    lines.value = res.receipt.lines.map(l => ({ ...l }))
    notes.value = res.receipt.notes ?? ''
    discount.value = res.receipt.discount ?? 0
    clientName.value = res.receipt.clientName
  }
  catch (e) {
    error.value = apiMessage(e, 'Could not load this receipt.')
  }
  finally {
    loading.value = false
  }
}

onMounted(load)

/*
 * 🪤 **Y al cambiar el centro, que en un refresh llega DESPUÉS.**
 *
 * `currentId` se resuelve de forma asíncrona al arrancar la sesión. Entrando por
 * el listado ya está puesto, pero recargando esta URL directamente `onMounted`
 * corre con el centro todavía sin resolver: `load` se iba por el `return` de
 * arriba y la pantalla quedaba con la barra de carga puesta para siempre. Con el
 * watch, la primera resolución dispara la carga real.
 */
watch(currentId, load)

// ---- Líneas ----

function addLine() {
  lines.value.push({
    source: 'manual',
    kind: 'other',
    label: '',
    unitAmount: 0,
    quantity: 1,
  })
}

function removeLine(index: number) {
  lines.value.splice(index, 1)
}

/*
 * El panel de selección de offerings.
 *
 * Antes cada fila tenía un `VSelect` en la columna "From": no se podía buscar
 * —con treinta ofertas hay que recorrer la lista— y ocupaba una columna entera
 * para algo que se usa una vez por línea. Ahora el rótulo es un campo de texto
 * libre con un botón redondo al costado, y ese botón abre este panel: se busca,
 * se eligen **una o varias**, y cada una entra como su propia línea.
 */
const pickerOpen = ref(false)
const pickerSearch = ref('')
const pickerSelected = ref<string[]>([])

/** Qué fila disparó el panel: la primera elegida la completa. */
const pickerRow = ref<number | null>(null)

/**
 * Filtro por tipo, encima del buscador.
 *
 * Dos chips que se marcan y no un desplegable: son dos opciones, y con el
 * catálogo entero mezclado hay que recorrer la lista para encontrar un alquiler
 * entre veinte servicios. Ninguno marcado = todo, que es lo mismo que marcar los
 * dos y una tilde menos.
 */
const pickerKinds = ref<OfferingOption['kind'][]>([])

const pickerResults = computed(() => {
  const q = pickerSearch.value.trim().toLowerCase()
  const kinds = pickerKinds.value

  return offeringOptions.value.filter(o =>
    (!q || o.title.toLowerCase().includes(q))
    && (!kinds.length || kinds.includes(o.kind)))
})

function togglePickerKind(kind: OfferingOption['kind']) {
  pickerKinds.value = pickerKinds.value.includes(kind)
    ? pickerKinds.value.filter(k => k !== kind)
    : [...pickerKinds.value, kind]
}

function openPicker(index: number) {
  if (!editable.value)
    return

  pickerRow.value = index
  pickerSearch.value = ''
  pickerKinds.value = []
  pickerSelected.value = []
  pickerOpen.value = true
}

function togglePick(id: string) {
  const i = pickerSelected.value.indexOf(id)

  if (i >= 0)
    pickerSelected.value.splice(i, 1)
  else
    pickerSelected.value.push(id)
}

/** Una offering, ya como línea del recibo. */
function lineFromOffering(o: OfferingOption): ReceiptLine {
  return {
    source: 'manual',
    offeringId: o.id,
    kind: o.kind === 'rental' ? 'rental' : 'activity',
    label: o.title,
    unitAmount: o.amount,
    quantity: 1,
  }
}

/**
 * Aplica lo elegido: la primera completa la fila que abrió el panel y el resto
 * se agregan debajo.
 *
 * Así elegir una sola cosa se siente como completar el renglón en el que estabas
 * —que es el caso corriente— y elegir cinco no obliga a repetir el gesto cinco
 * veces.
 */
function applyPicker() {
  const picked = pickerSelected.value
    .map(id => offeringOptions.value.find(o => o.id === id))
    .filter((o): o is OfferingOption => !!o)

  if (!picked.length) {
    pickerOpen.value = false

    return
  }

  const row = pickerRow.value
  const [first, ...rest] = picked

  if (row != null && lines.value[row])
    lines.value[row] = lineFromOffering(first)
  else
    lines.value.push(lineFromOffering(first))

  const at = row != null ? row + 1 : lines.value.length

  lines.value.splice(at, 0, ...rest.map(lineFromOffering))

  pickerOpen.value = false
}

// ---- Guardar / cobrar ----

/**
 * Guarda. Si es un borrador nuevo lo **crea** y reemplaza la URL.
 *
 * `router.replace` y no `push`: la ruta `/new` deja de existir en cuanto el
 * documento tiene id, y dejarla en el historial haría que el botón de atrás
 * abriera un borrador vacío del mismo cliente.
 */
async function save(silent = false) {
  const dcId = currentId.value

  if (!dcId || !editable.value)
    return false

  saving.value = true
  try {
    // `amount` no se manda: lo calcula el servidor. Un total que llega del
    // navegador es un total que se puede editar desde las herramientas de
    // desarrollo, y esto es el comprobante de un cobro.
    const payload = {
      lines: lines.value.map(({ amount, ...l }) => l),
      discount: appliedDiscount.value,
      notes: notes.value.trim(),
    }

    if (isNew.value) {
      const { receipt: created } = await createReceipt(dcId, {
        ...payload,
        clientId: clientId.value,
      })

      receipt.value = created
      receiptId.value = created.id
      await router.replace(`/sales/receipts/${created.id}`)
    }
    else {
      const { receipt: saved } = await updateReceipt(dcId, receiptId.value, payload)

      receipt.value = saved
    }

    if (!silent) {
      notifySuccess('Receipt saved.')

      // Se vuelve al listado: guardar es terminar con este recibo. Quedarse en
      // el formulario invita a seguir editando algo que ya se dio por cerrado.
      router.push('/sales/receipts')
    }

    return true
  }
  catch (e) {
    notifyError(apiMessage(e, 'Could not save the receipt.'))

    return false
  }
  finally {
    saving.value = false
  }
}

/**
 * Descartar.
 *
 * Un borrador sin guardar se va sin preguntarle a nadie —no existe— y uno ya
 * guardado se borra de verdad: todavía no tiene número ni escribió en ninguna
 * reserva, así que dejarlo como "anulado" llenaría el listado de papeles que
 * nunca fueron nada.
 */
async function discard() {
  const dcId = currentId.value

  if (!dcId)
    return

  if (isNew.value) {
    router.push('/sales/receipts')

    return
  }

  const ok = await confirmRef.value?.open({
    title: 'Discard this draft?',
    message: 'It is removed completely. Its activities stay unpaid and can go on another receipt.',
    confirmText: 'Discard',
    confirmColor: 'error',
  })

  if (!ok)
    return

  saving.value = true
  try {
    await deleteReceipt(dcId, receiptId.value)
    notifySuccess('Draft discarded.')
    router.push('/sales/receipts')
  }
  catch (e) {
    notifyError(apiMessage(e, 'Could not discard the draft.'))
  }
  finally {
    saving.value = false
  }
}

async function markPaid() {
  const dcId = currentId.value

  if (!dcId)
    return

  const ok = await confirmRef.value?.open({
    title: 'Mark as paid?',
    message: `${money(total.value)} from ${receipt.value?.clientName}. `
      + 'The receipt is locked after this, and its activities are marked as paid.',
    confirmText: 'Mark as paid',
  })

  if (!ok)
    return

  // Se guarda primero: cobrar un recibo con líneas sin guardar cobraría el
  // estado viejo, que es distinto del que el usuario está mirando.
  if (!(await save(true)))
    return

  saving.value = true
  try {
    const { receipt: paid } = await payReceipt(dcId, receiptId.value)

    receipt.value = paid
    lines.value = paid.lines.map(l => ({ ...l }))
    notifySuccess(`Receipt ${paid.number} paid.`)
    router.push('/sales/receipts')
  }
  catch (e) {
    notifyError(apiMessage(e, 'Could not mark the receipt as paid.'))
  }
  finally {
    saving.value = false
  }
}

async function onVoid() {
  const dcId = currentId.value

  if (!dcId)
    return

  const ok = await confirmRef.value?.open({
    title: 'Void this receipt?',
    message: 'Its activities go back to unpaid so they can go on a new receipt. '
      + 'The voided receipt keeps its number.',
    confirmText: 'Void',
    confirmColor: 'error',
  })

  if (!ok)
    return

  saving.value = true
  try {
    const { receipt: voided } = await voidReceipt(dcId, receiptId.value)

    receipt.value = voided
    notifySuccess('Receipt voided.')
  }
  catch (e) {
    notifyError(apiMessage(e, 'Could not void the receipt.'))
  }
  finally {
    saving.value = false
  }
}

/** Trae lo que haya quedado sin cobrar desde que se abrió el recibo. */
async function pullOutstanding() {
  const dcId = currentId.value
  const client = receipt.value?.clientId ?? clientId.value

  if (!dcId || !client)
    return

  saving.value = true
  try {
    const outstanding = await getOutstandingCharges(dcId, client)
    const already = new Set(lines.value.map(l => l.bookingId).filter(Boolean))

    const fresh = outstanding.activities
      .flatMap(a => a.lines)
      .filter(l => !already.has(l.bookingId))

    if (!fresh.length) {
      notifySuccess('Nothing new to add.')

      return
    }

    lines.value.push(...fresh)
    notifySuccess(`${fresh.length} line${fresh.length === 1 ? '' : 's'} added.`)
  }
  catch (e) {
    notifyError(apiMessage(e, 'Could not load outstanding activities.'))
  }
  finally {
    saving.value = false
  }
}

const pdfLoading = ref(false)

async function openPdf() {
  const dcId = currentId.value

  if (!dcId)
    return

  const tab = window.open('', '_blank')

  pdfLoading.value = true
  try {
    const { url } = await createReceiptPdfLink(dcId, receiptId.value)

    const absolute = new URL(url, apiOrigin()).toString()

    if (tab)
      tab.location.href = absolute
    else
      window.open(absolute, '_blank')
  }
  catch (e) {
    tab?.close()
    notifyError(apiMessage(e, 'Could not open the receipt.'))
  }
  finally {
    pdfLoading.value = false
  }
}

const STATUS_CHIP = {
  draft: { label: 'Draft', color: 'secondary' },
  paid: { label: 'Paid', color: 'success' },
  void: { label: 'Void', color: 'error' },
} as const
</script>

<template>
  <div>
    <VProgressLinear
      v-if="loading"
      indeterminate
      color="primary"
    />

    <AlertBox
      variant="danger"
      :message="error"
    />

    <VCard v-if="receipt || (isNew && !loading)">
      <VCardItem>
        <VCardTitle class="d-flex align-center ga-3">
          <!--
            El número aparece al cobrar: hasta entonces esto es un borrador y
            no tiene por qué gastar un correlativo.
          -->
          {{ receipt?.number ? `Receipt ${receipt.number}` : 'New receipt' }}
          <VChip
            :color="STATUS_CHIP[receipt?.status ?? 'draft'].color"
            size="small"
          >
            {{ STATUS_CHIP[receipt?.status ?? 'draft'].label }}
          </VChip>
          <VChip
            v-if="isNew"
            size="small"
            variant="tonal"
            color="warning"
          >
            Unsaved
          </VChip>
        </VCardTitle>
        <VCardSubtitle>
          {{ clientName }}
          <template v-if="receipt?.paidAt">
            · Paid {{ formatDate(receipt.paidAt) }}
          </template>
        </VCardSubtitle>

        <template #append>
          <VBtn
            variant="text"
            size="small"
            prepend-icon="ri-arrow-left-line"
            to="/sales/receipts"
          >
            All receipts
          </VBtn>
        </template>
      </VCardItem>

      <VDivider />

      <VCardText>
        <!--
          Las líneas como TABLA y no como tarjetas de campos: son diez o veinte,
          salen de actividades ya reservadas, y lo que hace quien cobra es
          leerlas de corrido y compararlas.
        -->
        <VTable class="text-no-wrap bt-affix-aligned">
          <!-- `bt-affix-aligned`: ver la nota de la hoja de estilos al pie. -->
          <thead>
            <tr>
              <th style="min-inline-size: 240px">
                Item
              </th>
              <!--
                Los tres números a la derecha. Los campos se estrechan y se
                empujan a ese borde (`bt-num`) en vez de ocupar la columna
                entera: un input ancho con la cifra alineada al final deja el
                prefijo de la moneda descolgado en la otra punta.
              -->
              <th
                class="text-end"
                style="inline-size: 140px"
              >
                Unit price
              </th>
              <th
                class="text-end"
                style="inline-size: 90px"
              >
                Qty
              </th>
              <th
                class="text-end"
                style="inline-size: 120px"
              >
                Amount
              </th>
              <th style="inline-size: 48px" />
            </tr>
          </thead>
          <tbody>
            <!--
              Una línea de separación al cambiar de día, y nada más: una fila de
              cabecera por día costaba una fila entera por día, que es justo lo
              que sobraba.
            -->
            <tr
              v-for="row in rows"
              :key="row.index"
              :class="row.firstOfDay ? 'bt-day-start' : ''"
            >
              <td :class="row.child ? 'ps-8' : ''">
                <!--
                  Texto libre + el botón que abre el catálogo. El rótulo es lo
                  que se imprime, así que tiene que poder escribirse a mano
                  aunque venga de una oferta: el mostrador a veces aclara algo
                  ("Tank ×2 — nitrox").
                -->
                <!--
                  El avatar + dos líneas de la tabla de la plantilla
                  (`DemoDataTableCellSlot`): ícono de 32 a la izquierda, el
                  concepto en `font-weight-medium text-high-emphasis` y el detalle
                  en `<small>` debajo. Reemplaza al chip de tipo, que ocupaba un
                  renglón para decir lo mismo que dice el ícono.
                -->
                <div class="d-flex align-center">
                  <VTooltip
                    :text="lineKind(row.line).label"
                    location="top"
                  >
                    <template #activator="{ props: tip }">
                      <VAvatar
                        v-bind="tip"
                        size="32"
                        :color="lineKind(row.line).color"
                        variant="tonal"
                        rounded
                      >
                        <VIcon
                          :icon="lineKind(row.line).icon"
                          size="18"
                        />
                      </VAvatar>
                    </template>
                  </VTooltip>

                  <div class="d-flex flex-column ms-3 flex-grow-1">
                    <!--
                      Una línea que viene de una reserva no se edita, así que es
                      texto: el campo vacío alrededor de algo que no se puede
                      tocar invita a intentarlo. El rótulo de una manual sí tiene
                      que poder escribirse a mano —el mostrador a veces aclara
                      algo ("Tank ×2 — nitrox")— y lleva además el botón que abre
                      el catálogo.
                    -->
                    <span
                      v-if="!editable || row.line.source === 'booking'"
                      class="d-block font-weight-medium text-high-emphasis text-truncate"
                    >{{ row.line.label }}</span>

                    <VTextField
                      v-else
                      v-model="row.line.label"
                      density="compact"
                      hide-details
                      placeholder="Description"
                      variant="underlined"
                    >
                      <template #append-inner>
                        <VBtn
                          icon="ri-shopping-bag-3-line"
                          variant="tonal"
                          color="primary"
                          size="x-small"
                          density="comfortable"
                          title="Pick from services and rentals"
                          @click="openPicker(row.index)"
                        />
                      </template>
                    </VTextField>

                    <small v-if="lineDetail(row.line, row.child)">{{ lineDetail(row.line, row.child) }}</small>
                  </div>
                </div>
              </td>
              <td class="text-end">
                <!--
                  `underlined` y no `plain`: la línea de abajo es lo único que
                  distingue un campo de un número impreso, y en una tabla de
                  veinte renglones sin ella no se ve qué se puede tocar. El
                  prefijo de la moneda va en el precio y no en el total, que es
                  calculado — ver `currencyPrefix`, que sigue la preferencia (§35).

                  🪤 Sin `reverse`. Esa prop invierte la dirección del campo, así
                  que manda el prefijo al otro lado: el símbolo salía a la derecha
                  del número y levantado, como si fuera un sufijo. El ejemplo de
                  la plantilla no la usa — prefijo y número, de izquierda a
                  derecha.
                -->
                <!--
                  Los que llegaron sin precio en el catálogo se pintan: un cero
                  entre veinte renglones no se distingue de un cero querido, y
                  cobrarlo así es cobrar de menos sin que nadie lo note.
                -->
                <VTextField
                  v-model.number="row.line.unitAmount"
                  class="bt-num bt-num--price"
                  type="number"
                  density="compact"
                  hide-details
                  :readonly="!editable"
                  variant="underlined"
                  :prefix="currencyPrefix(currency)"
                  :color="row.line.needsPrice ? 'warning' : undefined"
                  :base-color="row.line.needsPrice ? 'warning' : undefined"
                  :placeholder="row.line.needsPrice ? 'Set price' : undefined"
                />
              </td>
              <td class="text-end">
                <VTextField
                  v-model.number="row.line.quantity"
                  class="bt-num bt-num--qty"
                  type="number"
                  min="1"
                  density="compact"
                  hide-details
                  :readonly="!editable"
                  variant="underlined"
                />
              </td>
              <td class="text-end font-weight-medium">
                {{ money(row.line.unitAmount * row.line.quantity) }}
              </td>
              <td class="text-end">
                <VBtn
                  v-if="editable"
                  icon="ri-delete-bin-line"
                  variant="text"
                  size="small"
                  color="error"
                  title="Remove line"
                  @click="removeLine(row.index)"
                />
              </td>
            </tr>

            <tr v-if="!lines.length">
              <td
                colspan="6"
                class="text-center text-medium-emphasis py-6"
              >
                No lines yet.
              </td>
            </tr>
          </tbody>
        </VTable>

        <div
          v-if="editable"
          class="d-flex flex-wrap ga-2 mt-4"
        >
          <VBtn
            variant="tonal"
            size="small"
            prepend-icon="ri-add-line"
            @click="addLine"
          >
            Add line
          </VBtn>
          <VBtn
            variant="text"
            size="small"
            prepend-icon="ri-refresh-line"
            :loading="saving"
            title="Bring in anything booked since this receipt was started"
            @click="pullOutstanding"
          >
            Pull new activities
          </VBtn>
        </div>

        <VDivider class="my-6" />

        <VRow>
          <VCol
            cols="12"
            md="7"
          >
            <VTextarea
              v-model="notes"
              label="Notes"
              rows="3"
              :readonly="!editable"
              persistent-hint
              hint="Printed on the receipt."
            />
          </VCol>
          <VCol
            cols="12"
            md="5"
            class="d-flex align-end justify-end"
          >
            <div
              class="text-end"
              style="min-inline-size: 240px"
            >
              <!--
                El subtotal solo aparece cuando hay descuento: sin él sería el
                total escrito dos veces.
              -->
              <div
                v-if="appliedDiscount"
                class="d-flex justify-space-between text-body-2 text-medium-emphasis"
              >
                <span>Subtotal</span>
                <span>{{ money(subtotal) }}</span>
              </div>

              <div class="d-flex align-center justify-space-between ga-3 my-2 bt-affix-aligned">
                <span class="text-body-2 text-medium-emphasis">Discount</span>
                <!--
                  A la derecha como los de la tabla: es el otro importe que
                  se escribe a mano, y está en la misma columna que Subtotal y
                  Total, que ya venían alineados a ese borde.
                -->
                <VTextField
                  v-model.number="discount"
                  class="bt-num bt-num--discount"
                  type="number"
                  min="0"
                  density="compact"
                  hide-details
                  variant="underlined"
                  :readonly="!editable"
                  :prefix="currencyPrefix(currency)"
                />
              </div>

              <VDivider class="my-2" />

              <div class="d-flex justify-space-between align-baseline">
                <span class="text-caption text-medium-emphasis">Total</span>
                <span class="text-h4">{{ money(total) }}</span>
              </div>
            </div>
          </VCol>
        </VRow>
      </VCardText>

      <VDivider />

      <VCardActions class="px-4 py-3 d-flex flex-wrap ga-2">
        <!-- Sin documento guardado no hay comprobante que mirar ni emitir. -->
        <VBtn
          v-if="!isNew"
          variant="outlined"
          prepend-icon="ri-eye-line"
          :to="`/sales/receipts/${receiptId}/preview`"
        >
          Preview
        </VBtn>
        <VBtn
          v-if="!isNew"
          variant="text"
          prepend-icon="ri-file-pdf-2-line"
          :loading="pdfLoading"
          @click="openPdf"
        >
          Receipt PDF
        </VBtn>

        <!--
          Un borrador se DESCARTA (se borra); uno cobrado se ANULA (conserva su
          número y devuelve las reservas). Son dos cosas distintas y por eso no
          comparten botón.
        -->
        <VBtn
          v-if="editable"
          variant="text"
          color="error"
          :loading="saving"
          @click="discard"
        >
          Discard
        </VBtn>
        <VBtn
          v-else-if="receipt && receipt.status !== 'void'"
          variant="text"
          color="error"
          :loading="saving"
          @click="onVoid"
        >
          Void
        </VBtn>

        <VSpacer />

        <template v-if="editable">
          <VBtn
            variant="text"
            :loading="saving"
            @click="save"
          >
            Save draft
          </VBtn>
          <VBtn
            color="success"
            prepend-icon="ri-money-euro-circle-line"
            :loading="saving"
            :disabled="!lines.length"
            @click="markPaid"
          >
            Mark as paid
          </VBtn>
        </template>
      </VCardActions>
    </VCard>

    <!--
      Panel de selección de offerings.

      Va en la RAÍZ de la página y no dentro de la tarjeta: un
      `VNavigationDrawer` dentro de una tarjeta con su propio contexto de
      apilado queda recortado y con el velo puesto solo sobre ella (§24.8).
    -->
    <VNavigationDrawer
      v-model="pickerOpen"
      temporary
      location="end"
      :width="$vuetify.display.smAndDown ? undefined : 460"
      class="bt-picker"
    >
      <div class="d-flex align-center justify-space-between pa-4">
        <div>
          <div class="text-h6">
            Add from the catalogue
          </div>
          <div class="text-caption text-medium-emphasis">
            Pick one or several — each one becomes its own line.
          </div>
        </div>
        <VBtn
          icon="ri-close-line"
          variant="text"
          size="small"
          @click="pickerOpen = false"
        />
      </div>

      <VDivider />

      <div class="pa-4">
        <VTextField
          v-model="pickerSearch"
          placeholder="Search services and rentals"
          prepend-inner-icon="ri-search-line"
          clearable
          hide-details
          autofocus
        />

        <!--
          Debajo del buscador y no al lado: el ancho del panel no alcanza para
          los dos, y con el buscador arriba el filtro se lee como lo que es,
          un acotamiento de lo que se está buscando.
        -->
        <div class="d-flex ga-2 mt-3">
          <VChip
            :color="pickerKinds.includes('service') ? 'primary' : undefined"
            :variant="pickerKinds.includes('service') ? 'flat' : 'outlined'"
            size="small"
            @click="togglePickerKind('service')"
          >
            Services
          </VChip>
          <VChip
            :color="pickerKinds.includes('rental') ? 'primary' : undefined"
            :variant="pickerKinds.includes('rental') ? 'flat' : 'outlined'"
            size="small"
            @click="togglePickerKind('rental')"
          >
            Rentals
          </VChip>
        </div>
      </div>

      <VList
        class="py-0"
        density="comfortable"
      >
        <!--
          La fila entera alterna la selección; la casilla es indicador.
          Apuntarle a un cuadrado de 18 px cinco veces es trabajo de más
          (mismo criterio que el picker de clientes del roster, §20.10).
        -->
        <VListItem
          v-for="o in pickerResults"
          :key="o.id"
          @click="togglePick(o.id)"
        >
          <template #prepend>
            <VCheckboxBtn
              :model-value="pickerSelected.includes(o.id)"
              color="primary"
            />
          </template>

          <VListItemTitle>{{ o.title }}</VListItemTitle>
          <VListItemSubtitle>
            <VChip
              :color="o.kind === 'rental' ? 'info' : 'primary'"
              size="x-small"
              variant="tonal"
              class="me-2"
            >
              {{ o.kind === 'rental' ? 'Rental' : 'Service' }}
            </VChip>
            <span v-if="o.amount">{{ money(o.amount) }}</span>
            <span
              v-else
              class="text-disabled"
            >No price</span>
          </VListItemSubtitle>
        </VListItem>

        <VListItem v-if="!pickerResults.length">
          <VListItemTitle class="text-medium-emphasis">
            Nothing matches that.
          </VListItemTitle>
        </VListItem>
      </VList>

      <template #append>
        <VDivider />
        <div class="d-flex align-center ga-2 pa-4">
          <span class="text-caption text-medium-emphasis">
            {{ pickerSelected.length }} selected
          </span>
          <VSpacer />
          <VBtn
            variant="text"
            @click="pickerOpen = false"
          >
            Cancel
          </VBtn>
          <VBtn
            :disabled="!pickerSelected.length"
            @click="applyPicker"
          >
            Add
          </VBtn>
        </div>
      </template>
    </VNavigationDrawer>

    <ConfirmDialog ref="confirmRef" />
  </div>
</template>

<style scoped>
/*
 * 🪤 `.v-navigation-drawer__content` viene con `overflow-y: hidden` (§20.7): sin
 * esto, un catálogo de treinta ofertas no se puede recorrer y el botón Add queda
 * fuera de alcance en una pantalla baja.
 */
.bt-picker :deep(.v-navigation-drawer__content) {
  overflow-y: auto;
}
</style>

<style lang="scss">
/*
 * El cambio de día: una línea algo más marcada que la de entre renglones, y nada
 * más. Separa los grupos sin costar una fila ni pelearle atención a lo que se lee.
 */
.bt-day-start td {
  border-block-start: 2px solid rgba(var(--v-border-color), 0.28);
}

/*
 * El prefijo de la moneda, alineado con el número.
 *
 * 🪤 No son las variables de padding: para `underlined` + `compact`,
 * `--v-field-padding-top` y `--v-field-input-padding-top` terminan valiendo lo
 * mismo, así que igualarlas no movía nada. Lo que descuadra es la ALTURA de la
 * caja: la plantilla fija el input en 40px por densidad
 * (`@core/scss/template/libs/vuetify/components/_field.scss`) y no toca el
 * afijo, que se queda con los 32px que Vuetify le da al compact sin marco. Como
 * `.v-field__field` alinea a `flex-start`, la caja del prefijo queda 8px más
 * baja y su texto —centrado dentro de ella— 4px más arriba que la cifra.
 *
 * `align-self: stretch` la lleva a la altura de la fila en lugar de repetir el
 * 40px de la plantilla: sigue valiendo si cambia la densidad o ese número.
 */
.bt-affix-aligned {
  .v-text-field__prefix,
  .v-text-field__suffix {
    align-self: stretch;
  }
}

/*
 * Los campos numéricos de la tabla, a la derecha.
 *
 * 🪤 No alcanza con alinear el TEXTO: el input ocupa la columna entera, así que
 * la cifra se va al borde y el prefijo de la moneda se queda en el otro, con el
 * ancho de la columna de separación. Se estrecha el campo hasta lo que la cifra
 * necesita y se lo empuja al borde con el `text-end` de la celda; adentro el
 * texto también va al final, para que las unidades queden en la misma columna.
 *
 * `inline-grid` porque `.v-input` es `grid`, y un bloque ignora el `text-align`
 * de la celda que lo contiene. Sin `reverse`, que mandaría el prefijo al otro
 * lado (§45.1).
 */
.bt-num {
  display: inline-grid;
  vertical-align: middle;

  input {
    text-align: end;
  }
}

.bt-num--price { inline-size: 92px; }
.bt-num--qty { inline-size: 52px; }
.bt-num--discount { inline-size: 110px; }
</style>
